import { useEffect, useState } from 'react';

/**
 * Hook to track whether an anchor element is fully visible in the viewport.
 * Uses IntersectionObserver to detect when the anchor scrolls out of view or becomes obscured.
 * Polls for element availability to handle cases where the ref is populated after mount.
 *
 * @param anchorRef - Reference to the anchor element to observe
 * @returns boolean indicating whether the anchor is fully visible
 */
export const useAnchorVisibility = (anchorRef: React.RefObject<HTMLElement>): boolean => {
  const [isAnchorVisible, setIsAnchorVisible] = useState(true);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let checkInterval: NodeJS.Timeout | null = null;

    const setupObserver = (element: HTMLElement) => {
      observer = new IntersectionObserver(
        ([entry]) => {
          // Track if the anchor is fully visible
          const isVisible = entry?.isIntersecting && entry.intersectionRatio === 1;
          setIsAnchorVisible(!!isVisible);
        },
        {
          threshold: [0, 1], // Trigger when element enters/exits or becomes fully visible/hidden
          rootMargin: '0px',
        }
      );

      observer.observe(element);
      if (checkInterval) {
        clearInterval(checkInterval);
        checkInterval = null;
      }
    };

    // If element is available immediately, set up observer
    if (anchorRef.current) {
      setupObserver(anchorRef.current);
    } else {
      // Otherwise, poll until element becomes available
      checkInterval = setInterval(() => {
        if (anchorRef.current) {
          setupObserver(anchorRef.current);
        }
      }, 100);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [anchorRef]);

  return isAnchorVisible;
};
